export const MAX_UPLOAD_FILE_SIZE = 7_000_000;

export type DownloadFileFailure =
  | { ok: false; reason: 'missing-url' }
  | { ok: false; reason: 'request-failed' }
  | { ok: false; reason: 'http-error'; status: number }
  | { ok: false; reason: 'browser-failed' };

export type DownloadFileResult = { ok: true } | DownloadFileFailure;

const DOWNLOAD_TIMEOUT_MS = 30_000;

export async function downloadFile(
  fileName: string,
  fileUrl: string | undefined | null
): Promise<DownloadFileResult> {
  if (!fileUrl) return { ok: false, reason: 'missing-url' };

  let memoryUrl: string | undefined;
  let anchor: HTMLAnchorElement | undefined;
  let response: Response;
  let blob: Blob;
  const controller = new AbortController();
  let timeoutID = window.setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);

  try {
    response = await fetch(fileUrl, { signal: controller.signal });
    window.clearTimeout(timeoutID);
    if (!response.ok) return { ok: false, reason: 'http-error', status: response.status };

    if (!response.body) {
      timeoutID = window.setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);
      blob = await response.blob();
    } else {
      const reader = response.body.getReader();
      const chunks: BlobPart[] = [];

      try {
        while (true) {
          timeoutID = window.setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);
          const { done, value } = await reader.read();
          window.clearTimeout(timeoutID);
          if (done) break;
          chunks.push(value);
        }
      } finally {
        reader.releaseLock();
      }

      blob = new Blob(chunks, { type: response.headers.get('content-type') ?? '' });
    }
  } catch {
    return { ok: false, reason: 'request-failed' };
  } finally {
    window.clearTimeout(timeoutID);
  }

  try {
    memoryUrl = window.URL.createObjectURL(blob);
    anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = memoryUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    return { ok: true };
  } catch {
    return { ok: false, reason: 'browser-failed' };
  } finally {
    anchor?.remove();
    if (memoryUrl) window.URL.revokeObjectURL(memoryUrl);
  }
}
